import { jsPDF } from 'jspdf'

// Brand colors
const BRAND_PRIMARY = [30, 64, 175]    // #1e40af
const BRAND_ACCENT = [14, 165, 233]    // #0ea5e9
const TEXT_DARK = [15, 23, 42]         // #0f172a
const TEXT_MUTED = [100, 116, 139]     // #64748b
const BORDER_COLOR = [226, 232, 240]   // #e2e8f0
const SUCCESS_BG = [240, 253, 244]     // #f0fdf4
const SUCCESS_BORDER = [34, 197, 94]   // #22c55e
const WHITE = [255, 255, 255]

const PAGE_WIDTH = 210 // A4 mm
const MARGIN = 20
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2

/**
 * Generate a professional PDF for an accepted freight quote.
 * @param {Object} rfq - Transformed request object
 * @param {Object|null} carrier - Full carrier details (or null)
 */
export const generateQuotePDF = (rfq, carrier) => {
  const doc = new jsPDF('p', 'mm', 'a4')
  let y = MARGIN

  y = drawHeader(doc, y, rfq)
  y = drawCarrierSection(doc, y, rfq, carrier)
  y = drawRouteSection(doc, y, rfq)
  y = drawCargoSection(doc, y, rfq)
  y = drawQuoteSection(doc, y, rfq)
  y = drawNotesSection(doc, y, rfq)
  y = drawDescriptionSection(doc, y, rfq)
  drawFooter(doc)

  const shortId = (rfq.id || 'unknown').substring(0, 8)
  const date = new Date().toISOString().slice(0, 10)
  doc.save(`FreightQuote-${shortId}-${date}.pdf`)
}

// ── Header ──────────────────────────────────────────────────────────────

function drawHeader(doc, y, rfq) {
  // Brand bar
  doc.setFillColor(...BRAND_PRIMARY)
  doc.rect(0, 0, PAGE_WIDTH, 36, 'F')

  doc.setTextColor(...WHITE)
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.text('Frate.Cloud', MARGIN, 16)

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.text('Freight Quote Confirmation', MARGIN, 24)

  // Right-aligned ref and date
  const shortId = (rfq.id || '').substring(0, 8)
  doc.setFontSize(9)
  doc.text(`Ref: RFQ-${shortId}`, PAGE_WIDTH - MARGIN, 16, { align: 'right' })
  doc.text(`Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, PAGE_WIDTH - MARGIN, 22, { align: 'right' })

  // Status badge
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  const statusText = rfq.status || 'Accepted'
  const statusWidth = doc.getTextWidth(statusText) + 8
  doc.setFillColor(...SUCCESS_BORDER)
  doc.roundedRect(PAGE_WIDTH - MARGIN - statusWidth, 26, statusWidth, 7, 2, 2, 'F')
  doc.setTextColor(...WHITE)
  doc.text(statusText, PAGE_WIDTH - MARGIN - statusWidth + 4, 31)

  return 44
}

// ── Section helpers ─────────────────────────────────────────────────────

function drawSectionTitle(doc, y, title) {
  y = checkPageBreak(doc, y, 14)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(...BRAND_PRIMARY)
  doc.text(title, MARGIN, y)
  y += 1.5
  doc.setDrawColor(...BRAND_PRIMARY)
  doc.setLineWidth(0.5)
  doc.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y)
  return y + 5
}

function drawRow(doc, y, label, value) {
  if (value === null || value === undefined || value === '') return y
  y = checkPageBreak(doc, y, 7)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(...TEXT_MUTED)
  doc.text(label, MARGIN + 2, y)

  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...TEXT_DARK)

  const valueStr = String(value)
  const maxWidth = CONTENT_WIDTH - 55
  if (doc.getTextWidth(valueStr) > maxWidth) {
    const lines = doc.splitTextToSize(valueStr, maxWidth)
    doc.text(lines, MARGIN + 55, y)
    return y + lines.length * 4.5
  }
  doc.text(valueStr, MARGIN + 55, y)
  return y + 5.5
}

function checkPageBreak(doc, y, needed) {
  if (y + needed > 275) {
    doc.addPage()
    drawFooter(doc)
    return MARGIN
  }
  return y
}

// ── Carrier Section ─────────────────────────────────────────────────────

function drawCarrierSection(doc, y, rfq, carrier) {
  y = drawSectionTitle(doc, y, 'Carrier Information')

  if (carrier) {
    y = drawRow(doc, y, 'Carrier Name', carrier.name)
    y = drawRow(doc, y, 'Type', carrier.type)
    if (carrier.contactName && carrier.contactName !== carrier.name) {
      y = drawRow(doc, y, 'Contact Person', carrier.contactName)
    }
    if (carrier.email && !carrier.email.endsWith('@carrier.local')) {
      y = drawRow(doc, y, 'Email', carrier.email)
    }
    if (carrier.phone && carrier.phone !== '+10000000000') {
      y = drawRow(doc, y, 'Phone', carrier.phone)
    }
    if (carrier.website) {
      y = drawRow(doc, y, 'Website', carrier.website)
    }
  } else {
    y = drawRow(doc, y, 'Carrier', rfq.carrierName || 'Not available')
  }

  return y + 3
}

// ── Route Section ───────────────────────────────────────────────────────

function drawRouteSection(doc, y, rfq) {
  y = drawSectionTitle(doc, y, 'Route Details')

  y = drawRow(doc, y, 'Origin', rfq.originFull || rfq.origin)
  if (rfq.forkliftAtOrigin != null) {
    y = drawRow(doc, y, 'Forklift at Origin', rfq.forkliftAtOrigin ? 'Yes' : 'No')
  }
  y = drawRow(doc, y, 'Destination', rfq.destinationFull || rfq.destination)
  if (rfq.forkliftAtDestination != null) {
    y = drawRow(doc, y, 'Forklift at Dest.', rfq.forkliftAtDestination ? 'Yes' : 'No')
  }
  if (rfq.deadline) {
    y = drawRow(doc, y, 'Deadline', rfq.deadline)
  }

  return y + 3
}

// ── Cargo Section ───────────────────────────────────────────────────────

function drawCargoSection(doc, y, rfq) {
  const hasCargoData = rfq.shipmentType || rfq.commodityType || rfq.containerType ||
    rfq.weightKg != null || rfq.volumeCBM != null || rfq.quantity != null ||
    rfq.incoterms || rfq.readyDate || rfq.specialRequirements ||
    rfq.dangerousGoods != null || rfq.stackable != null

  if (!hasCargoData) return y

  y = drawSectionTitle(doc, y, 'Cargo Details')

  y = drawRow(doc, y, 'Shipment Type', rfq.shipmentType)
  y = drawRow(doc, y, 'Commodity', rfq.commodityType)
  y = drawRow(doc, y, 'Container', rfq.containerType)
  if (rfq.weightKg != null) {
    y = drawRow(doc, y, 'Weight', `${rfq.weightKg.toLocaleString()} kg`)
  }
  if (rfq.volumeCBM != null) {
    y = drawRow(doc, y, 'Volume', `${rfq.volumeCBM} CBM`)
  }
  if (rfq.quantity != null) {
    y = drawRow(doc, y, 'Quantity', `${rfq.quantity} x ${rfq.packageType || 'packages'}`)
  }
  if (rfq.lengthCM != null && rfq.widthCM != null && rfq.heightCM != null) {
    y = drawRow(doc, y, 'Dimensions', `${rfq.lengthCM} x ${rfq.widthCM} x ${rfq.heightCM} cm`)
  }
  y = drawRow(doc, y, 'Incoterms', rfq.incoterms)
  y = drawRow(doc, y, 'Ready Date', rfq.readyDate)
  if (rfq.dangerousGoods != null) {
    y = drawRow(doc, y, 'Dangerous Goods', rfq.dangerousGoods ? 'Yes' : 'No')
  }
  if (rfq.stackable != null) {
    y = drawRow(doc, y, 'Stackable', rfq.stackable ? 'Yes' : 'No')
  }
  y = drawRow(doc, y, 'Special Req.', rfq.specialRequirements)

  return y + 3
}

// ── Quote Section (highlighted box) ─────────────────────────────────────

function drawQuoteSection(doc, y, rfq) {
  y = drawSectionTitle(doc, y, 'Quote Information')

  // Calculate box height based on content
  let lines = 1 // price always present
  if (rfq.transitDays) lines++
  if (rfq.validUntil) lines++
  if (rfq.quoteTerms) {
    const termLines = doc.splitTextToSize(rfq.quoteTerms, CONTENT_WIDTH - 60)
    lines += Math.max(1, termLines.length)
  }
  const boxHeight = Math.max(30, lines * 7 + 16)

  y = checkPageBreak(doc, y, boxHeight + 4)

  // Green highlight box
  doc.setFillColor(...SUCCESS_BG)
  doc.setDrawColor(...SUCCESS_BORDER)
  doc.setLineWidth(0.4)
  doc.roundedRect(MARGIN, y - 2, CONTENT_WIDTH, boxHeight, 3, 3, 'FD')

  let innerY = y + 6

  // Price — large and bold
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.setTextColor(...TEXT_DARK)
  const priceText = rfq.quotedPriceFormatted || 'N/A'
  doc.text(priceText, MARGIN + CONTENT_WIDTH / 2, innerY, { align: 'center' })
  innerY += 3

  doc.setFontSize(8)
  doc.setTextColor(...TEXT_MUTED)
  const gstLabel = rfq.quoteIncludesGST === true ? 'Quoted Price (incl. GST)'
    : rfq.quoteIncludesGST === false ? 'Quoted Price (ex. GST)'
    : 'Quoted Price'
  doc.text(gstLabel, MARGIN + CONTENT_WIDTH / 2, innerY, { align: 'center' })
  innerY += 7

  // Details below price
  doc.setFontSize(9)
  if (rfq.transitDays) {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...TEXT_MUTED)
    doc.text('Transit Time:', MARGIN + 8, innerY)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...TEXT_DARK)
    doc.text(`${rfq.transitDays} days`, MARGIN + 55, innerY)
    innerY += 5.5
  }

  if (rfq.validUntil) {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...TEXT_MUTED)
    doc.text('Valid Until:', MARGIN + 8, innerY)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...TEXT_DARK)
    doc.text(rfq.validUntil, MARGIN + 55, innerY)
    innerY += 5.5
  }

  if (rfq.quoteTerms) {
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...TEXT_MUTED)
    doc.text('Terms:', MARGIN + 8, innerY)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...TEXT_DARK)
    const termLines = doc.splitTextToSize(rfq.quoteTerms, CONTENT_WIDTH - 60)
    doc.text(termLines, MARGIN + 55, innerY)
  }

  return y + boxHeight + 5
}

// ── Notes Section (carrier response, clarification) ─────────────────────

function drawNotesSection(doc, y, rfq) {
  const hasNotes = rfq.carrierResponseNote || rfq.clarificationNotes ||
    rfq.cargoDescription || rfq.fullDescription

  // Only show carrier/clarification notes here; description goes to its own section
  if (!rfq.carrierResponseNote && !rfq.clarificationNotes) return y

  y = drawSectionTitle(doc, y, 'Communication Notes')

  if (rfq.carrierResponseNote) {
    y = checkPageBreak(doc, y, 12)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(...TEXT_MUTED)
    doc.text('Carrier Response:', MARGIN + 2, y)
    y += 4.5
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...TEXT_DARK)
    const lines = doc.splitTextToSize(rfq.carrierResponseNote, CONTENT_WIDTH - 4)
    y = checkPageBreak(doc, y, lines.length * 4.5)
    doc.text(lines, MARGIN + 4, y)
    y += lines.length * 4.5 + 3
  }

  if (rfq.clarificationNotes) {
    y = checkPageBreak(doc, y, 12)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(...TEXT_MUTED)
    doc.text('Clarification Notes:', MARGIN + 2, y)
    y += 4.5
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...TEXT_DARK)
    const lines = doc.splitTextToSize(rfq.clarificationNotes, CONTENT_WIDTH - 4)
    y = checkPageBreak(doc, y, lines.length * 4.5)
    doc.text(lines, MARGIN + 4, y)
    y += lines.length * 4.5 + 3
  }

  return y + 2
}

// ── Description Section ─────────────────────────────────────────────────

function drawDescriptionSection(doc, y, rfq) {
  const text = rfq.aiDescription || rfq.fullDescription
  if (!text) return y

  const title = rfq.aiDescription ? 'AI Summary' : 'Original Request'
  y = drawSectionTitle(doc, y, title)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...TEXT_DARK)

  const lines = doc.splitTextToSize(text, CONTENT_WIDTH - 4)

  // Handle multi-page long descriptions
  for (let i = 0; i < lines.length; i++) {
    y = checkPageBreak(doc, y, 5)
    doc.text(lines[i], MARGIN + 2, y)
    y += 4.5
  }

  return y + 2
}

// ── Footer ──────────────────────────────────────────────────────────────

function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages()
  const currentPage = doc.internal.getCurrentPageInfo().pageNumber

  doc.setDrawColor(...BORDER_COLOR)
  doc.setLineWidth(0.3)
  doc.line(MARGIN, 282, PAGE_WIDTH - MARGIN, 282)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  doc.setTextColor(...TEXT_MUTED)
  doc.text(
    `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} | Frate.Cloud`,
    MARGIN,
    287
  )
  doc.text(
    `Page ${currentPage} of ${pageCount}`,
    PAGE_WIDTH - MARGIN,
    287,
    { align: 'right' }
  )
}
